import { SELECTION_TYPE } from '@commons/models/LocationPoint/constants';
import mapToLocationPoint from '@commons/models/LocationPoint/mappers/mapToLocationPoint';
import { LOCATION_ORIGIN, LOCATION_POINT_TYPE } from '@location/constants';
import LocationParcel from '@location/containers/LocationParcel';
import { ignore } from '@utils/object';
import PropTypes from 'prop-types';
import React from 'react';

// Export for tests purposes.
export const getInitialState = () => ({
  showLocationParcel: false,
  points: null,
  selectedPoint: null,
  origin: LOCATION_ORIGIN.SEARCH,
  shouldSetSearchedLocation: true,
  includeTrackingData: false,
  selectionType: SELECTION_TYPE.SELECTED, // tracking
});

const propsToIngore = [
  'isUserLogged',
  'setSearchedLocation',
  'trackAddressSubmittedStarted',
  'onGPSError',
  'trackConfirmLocationFromMap',
  'trackAddressSubmittedFinished',
  'trackMapLoaded',
];

const LocationFlowManagerHOC = (T, { unmount = true } = {}) =>
  class LocationFlowManager extends React.PureComponent {
    static propTypes = {
      isUserLogged: PropTypes.bool.isRequired,
      city: PropTypes.object,
      getCurrentSearchForm: PropTypes.object,
      hasSearchedLocation: PropTypes.bool.isRequired,
      setSearchedLocation: PropTypes.func.isRequired,
      setAddressInSearchForm: PropTypes.func.isRequired,
      trackAddressSubmittedStarted: PropTypes.func.isRequired,
      onGPSError: PropTypes.func.isRequired,
      trackConfirmLocationFromMap: PropTypes.func.isRequired,
      trackAddressSubmittedFinished: PropTypes.func.isRequired,
      trackMapClosed: PropTypes.func.isRequired,
      trackModalClosed: PropTypes.func.isRequired,
      trackModalOpened: PropTypes.func.isRequired,
      trackMapLoaded: PropTypes.func.isRequired,
      isDesktopLayout: PropTypes.bool.isRequired,
      forceForm: PropTypes.bool,
    };

    _isMounted = true;

    state = getInitialState({});

    componentDidMount() {
      this._isMounted = true;
    }

    componentWillUnmount() {
      this._isMounted = false;
    }

    setStateIfMounted = newState => {
      if (this._isMounted) this.setState(newState);
    };

    setOrigin = origin => {
      if (origin) this.setStateIfMounted({ origin });
    };

    // BEGINS: LOCATION PARCEL ==========>

    runParcelLocationFlow = async (props = {}) => {
      const { origin = '', shouldSetSearchedLocation = true } = props;
      this.setStateIfMounted({
        shouldSetSearchedLocation,
        showLocationParcel: true,
      });
      this.setOrigin(origin);
      return new Promise((resolve, reject) => {
        this.resolveFlow = resolve;
        this.rejectFlow = reject;
      });
    };

    onFinishLocation = async location => {
      const { setSearchedLocation } = this.props;

      if (this.resolveFlow) {
        const { shouldSetSearchedLocation, selectionType } = this.state;
        if (shouldSetSearchedLocation) {
          // TODO: Check this tracking event in LocationMap (getTrackingProps)
          // trackConfirmLocationFromMap({ trackingData, point });

          await setSearchedLocation(location, { selectionType });
        }
        this.setStateIfMounted({ showLocationParcel: false });
        this.resolveFlow(location);
        this.resolveFlow = null;
      }
    };

    onCancelParcel = () => {
      if (this.resolveFlow) {
        this.rejectFlow();
        this.resolveFlow = null;
        this.setStateIfMounted({ showLocationParcel: false });
      }
    };

    // <========== ENDS: PARCEL

    selectAddress = async ({ origin, address }) => {
      this.setOrigin(origin);

      const location = mapToLocationPoint(
        address,
        LOCATION_POINT_TYPE.USER_ADDRESS,
      );

      this.trackFinish(location, {}, !!location);
      return this.props.setSearchedLocation(location);
    };

    trackFinish = (point, trackingData, success = true) => {
      this.props.trackAddressSubmittedFinished({
        address: { ...(point || {}), ...trackingData },
        success,
      });
    };

    showWrappedComponent() {
      const { isDesktopLayout } = this.props;
      if (isDesktopLayout) return true;
      const { showMap, showLocationForm } = this.state;
      return !showMap && !showLocationForm;
    }

    getPropsToInyect() {
      return {
        ...ignore(this.props, propsToIngore),
        selectAddress: this.selectAddress,
        getLocationByParcel: this.runParcelLocationFlow,
      };
    }

    // TODO: Check trackMapLoaded

    render() {
      const { origin, showLocationParcel } = this.state;

      const showT = this.showWrappedComponent();
      const wrappedComponentProps = this.getPropsToInyect();

      return (
        <>
          {showLocationParcel && (
            <LocationParcel
              show={showLocationParcel}
              onSuccess={this.onFinishLocation}
              onCancel={this.onCancelParcel}
              origin={origin}
            />
          )}

          {unmount ? (
            showT && <T {...wrappedComponentProps} />
          ) : (
            <T {...wrappedComponentProps} show={showT} />
          )}
        </>
      );
    }
  };

export default LocationFlowManagerHOC;
